// exportRun.js — the Export tab's per-file loop, at module level so it survives leaving the tab.
// The tab builds the `jobs` (everything the loop needs, resolved at click time) and hands them over;
// state goes to exportRunStore.js. Behaviour is the loop that used to live in ExportTab.runExport.
import { beginRun, setRun, addExported, addErrorKey, finishRun } from './exportRunStore';

async function fetchBlob(url, onProgress) {
  const resp = await fetch(url, { mode: 'cors' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const contentLength = resp.headers.get('content-length');
  if (contentLength && resp.body && onProgress) {
    const total = parseInt(contentLength, 10);
    let received = 0;
    const reader = resp.body.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress(received / total);
    }
    return new Blob(chunks);
  }
  const blob = await resp.blob();
  if (onProgress) onProgress(1);
  return blob;
}

async function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 8000);
}

async function writeBlobToDir(dirHandle, filename, blob) {
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob); await writable.close();
}

async function saveFile({ dirHandle, blob, filename }) {
  if (dirHandle) { await writeBlobToDir(dirHandle, filename, blob); }
  else { await triggerDownload(blob, filename); await new Promise(r => setTimeout(r, 350)); }
}

const phase = (p) => setRun(s => Object.assign({}, s, { phase: p }));
const progress = (name) => (pct) => setRun(s => Object.assign({}, s, { current: `${name} — ${Math.round(pct * 100)}%`, phase: 'downloading' }));

// jobs: [{ key, index, name, entry, event }] · postEvent(event) is fire-and-forget (injected, it lives in ExportTab)
// onFinished: asks Studio to drop its renders after a clean run (see exportRunStore.finishRun)
export async function startExportRun({ jobs, dirHandle, postEvent, onFinished }) {
  beginRun(jobs.length);
  const errors = [], okKeys = [];
  for (let j = 0; j < jobs.length; j++) {
    const { key: k, index: i, name, entry, event } = jobs[j];
    setRun(s => Object.assign({}, s, { exportingKey: k, done: j, current: name, phase: 'preparing' }));
    try {
      let blob;
      if ((entry.isLocal || entry.isOriginal) && (entry.localUrl || entry.storedUrl)) {
        // blob: URLs die on page reload — ORIGINAL falls back to its stored upload
        try { if (!entry.localUrl) throw new Error('no blob'); blob = await (await fetch(entry.localUrl)).blob(); }
        catch (e) {
          if (!entry.storedUrl) throw e;
          phase('downloading');
          const head = await fetch(entry.storedUrl, { method: 'HEAD' }).catch(() => null);
          if (!head || !head.ok) { errors.push(`#${i + 1} — original no longer available. Re-add the source video in Studio.`); continue; }
          blob = await fetchBlob(entry.storedUrl, progress(name));
        }
      } else {
        const head = await fetch(entry.url, { method: 'HEAD' }).catch(() => null);
        if (!head || !head.ok) { errors.push(`#${i + 1} — file not found. Regenerate from Studio.`); continue; }
        phase('downloading');
        blob = await fetchBlob(entry.url, progress(name));
        if (blob.size < 1000) { errors.push(`#${i + 1} — invalid file (${blob.size}b).`); continue; }
      }
      phase('saving');
      await saveFile({ dirHandle, blob, filename: name });
      okKeys.push(k);
      postEvent(event);
      addExported(k);
    } catch (err) { errors.push(`#${i + 1} — ${err.message}`); addErrorKey(k); }
  }
  setRun(s => Object.assign({}, s, { done: jobs.length }));
  finishRun({ done: okKeys.length, total: jobs.length, errors, onFinished });
}
